import { API_BASE_URL } from "../constants";
import { ApiError, ApiErrorDetail } from "./api.errors";

const API_TIMEOUT_MS = 30_000;

// /public/reschedule/... is registered directly on the FastAPI app with no
// /api/v1 prefix (mirrors /verify/{prescription_id}) - derive the bare
// origin by stripping the baked-in /api/v1 suffix from API_BASE_URL.
const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, "");

/**
 * Unauthenticated client for the public reschedule flow. Deliberately does
 * NOT reuse api.client.ts's apiRequest, which always tries to attach a
 * Cognito bearer token if any session exists in the browser - a stale staff
 * session in the same browser has no reason to ever reach this endpoint.
 */
export async function publicApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_ORIGIN}${endpoint}`;
  const headers: Record<string, string> = {};
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    ...options,
    signal: options.signal ?? AbortSignal.timeout(API_TIMEOUT_MS),
    headers: { ...headers, ...(options.headers as Record<string, string>) },
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    let details: ApiErrorDetail = {};
    try {
      const body = await response.json();
      if (body.error?.message) {
        // Standard BaseAPIResponse envelope: { success: false, error: { code, message } }
        message = body.error.message;
        details = { code: body.error.code };
      } else if (body.detail && typeof body.detail === "object" && !Array.isArray(body.detail)) {
        // Dedicated handlers (e.g. TOKEN_ALREADY_CONSUMED) - { detail: { error, message } }
        message = body.detail.message || body.detail.error || "Request failed";
        if (body.detail.error) details = { code: body.detail.error };
      } else if (body.detail) {
        message = String(body.detail);
      }
    } catch { /* JSON parse failed — keep HTTP status fallback */ }
    throw new ApiError(message, response.status, details);
  }

  if (response.status === 204) return null as T;

  const data = await response.json();
  if (data && typeof data === "object" && "success" in data && "data" in data) {
    return data.data as T;
  }
  return data as T;
}
